/**
 * Утилиты для проверки удаленной организации
 */

// Коды ответов API. Сравниваем по ним, а не по тексту `message`:
// текст локализован (DEFAULT_COUNTRY), поэтому на монгольском инстансе
// проверка по русской строке не срабатывала бы (docs/11-i18n-audit.md, Этап 5).
const ORG_DELETED_CODE = 'ORG_DELETED'
const USER_DELETED_CODE = 'USER_DELETED'

const ORG_DELETED_STATUSES = [401, 404, 500]
const AUTH_STORAGE_KEYS = ['accessToken', 'refreshToken', 'user']

/** Код ошибки из ответа API. */
function errorCode(error) {
  return error?.response?.data?.code || null
}

/**
 * Проверяет, связана ли ошибка с удаленной организацией
 * @param {Error} error - объект ошибки
 * @returns {boolean} true если организация удалена
 */
export function checkIfOrgDeleted(error) {
  if (!error) return false

  const status = error?.response?.status

  if (!ORG_DELETED_STATUSES.includes(status)) return false

  return errorCode(error) === ORG_DELETED_CODE
}

/**
 * Очищает localStorage и опционально вызывает auth/logout в store.
 * @param {Object} context - контекст ($store и т.д.)
 */
function clearSession(context, skipDispatch = false) {
  AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key))
  localStorage.setItem('loggedIn', 'false')
  if (!skipDispatch) {
    const store = context.$store || context.store || window?.$nuxt?.$store
    if (store?.dispatch) store.dispatch('auth/logout').catch(() => {})
  }
}

/**
 * Очищает сессию и редиректит на /auth (удалённая орг или пользователь).
 * @param {Object} context - контекст ($store, $router, redirect)
 */
export function handleOrgDeleted(context = {}) {
  if (context.redirect) {
    clearSession(context)
    context.redirect('/auth')
    return
  }
  if (typeof window !== 'undefined') {
    clearSession(context, true)
    window.location.replace('/auth')
  } else {
    clearSession(context)
    const router = context.$router || context.router || window?.$nuxt?.$router
    if (router) router.push('/auth')
  }
}

/**
 * Проверяет ошибку на удалённую организацию, при совпадении вызывает handleOrgDeleted.
 * @param {Error} error - объект ошибки
 * @param {Object} context - контекст ($store, $router, redirect)
 * @returns {boolean} true если организация удалена и выполнена обработка
 */
export function checkOrgDeleted(error, context = {}) {
  if (!checkIfOrgDeleted(error)) return false
  handleOrgDeleted(context)
  return true
}

/** 403 из-за выключенного ЛК — орг есть, это не «нет организации». */
export function isLkDisabledError(error) {
  return (
    error?.response?.status === 403 &&
    error?.response?.data?.code === 'LK_ACCESS_DISABLED'
  )
}

/** 403 из-за временного пароля: пользователь обязан сменить его, доступ есть. */
export function isPasswordChangeRequiredError(error) {
  return (
    error?.response?.status === 403 &&
    error?.response?.data?.code === 'PASSWORD_CHANGE_REQUIRED'
  )
}

const USER_DELETED_STATUSES = [401, 404]

/**
 * Проверяет ошибку на удалённого пользователя (админ удалил аккаунт).
 * @param {Error} error - объект ошибки
 * @returns {boolean} true если пользователь удалён
 */
function checkIfUserDeleted(error) {
  if (!error) return false

  const status = error?.response?.status

  if (!USER_DELETED_STATUSES.includes(status)) return false

  return errorCode(error) === USER_DELETED_CODE
}

/**
 * Проверяет ошибку на удалённого пользователя (админ удалил аккаунт), при совпадении вызывает handleOrgDeleted.
 * @param {Error} error - объект ошибки
 * @param {Object} context - контекст ($store, $router, redirect)
 * @returns {boolean} true если пользователь удалён и выполнена обработка
 */
export function checkUserDeleted(error, context = {}) {
  if (!checkIfUserDeleted(error)) return false
  handleOrgDeleted(context)
  return true
}
